import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const SearchableDropdown = ({
  options = [],
  labelKey = "label",
  valueKey = "value",
  imageKey,
  placeholder = "Select...",
  selectedValue = null,
  onSelect = () => {},
  searchKeys = ["label"],
  displaySearch = true,
  startIconClass = "fa-solid fa-burger",
  iconColor = "text-primary",
  error = "",
  disabled = false, // whole dropdown disable (keeps previous behavior)
}) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState(options);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    const q = (search || "").toLowerCase();
    if (!q) {
      setFiltered(options);
      return;
    }
    setFiltered(
      options.filter((opt) =>
        searchKeys.some((k) =>
          String(opt[k] ?? "")
            .toLowerCase()
            .includes(q)
        )
      )
    );
  }, [search, options, searchKeys]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;

      setMenuStyle({
        left: rect.left + scrollX,
        top: rect.bottom + scrollY + 6,
        width: rect.width,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  const handleOptionClick = (opt) => {
    if (opt?.disabled) return; // do nothing for disabled options
    onSelect(opt);
    setOpen(false);
    setSearch("");
  };

  const renderMenu = () => {
    if (disabled) return null; // whole dropdown disabled

    return ReactDOM.createPortal(
      <div
        ref={menuRef}
        style={{
          position: "absolute",
          left: menuStyle.left,
          top: menuStyle.top,
          width: menuStyle.width,
        }}
        className="z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto"
      >
        {displaySearch && (
          <div className="p-2 border-b sticky top-0 bg-white z-50">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          </div>
        )}

        <div style={{ maxHeight: 260, overflowY: "auto" }}>
          {filtered.length ? (
            filtered.map((opt) => {
              const isSelected = selectedValue && selectedValue[valueKey] === opt[valueKey];
              const isDisabled = !!opt.disabled;

              return (
                <div
                  key={String(opt[valueKey])}
                  onClick={() => handleOptionClick(opt)}
                  role="button"
                  aria-disabled={isDisabled}
                  className={`px-4 py-2 flex items-center gap-3 ${
                    isDisabled ? "text-gray-400 cursor-not-allowed bg-gray-50" : "cursor-pointer hover:bg-primary/10"
                  } ${isSelected && !isDisabled ? "bg-blue-50" : ""}`}
                >
                  {imageKey && opt[imageKey] && (
                    <img src={opt[imageKey]} alt={opt[labelKey]} className="w-8 h-8 rounded-md object-cover" />
                  )}

                  <div className={`flex-1 truncate ${isDisabled ? "opacity-80" : "text-gray-700"}`}>
                    {opt[labelKey]}
                  </div>

                  {/* show selected tick only if not disabled */}
                  {!isDisabled && isSelected && <i className="fa-solid fa-check text-blue-500 text-sm ml-auto"></i>}

                  {/* optional disabled icon */}
                  {isDisabled && <i className="fa-solid fa-lock ml-2 text-sm text-gray-400" />}
                </div>
              );
            })
          ) : (
            <div className="px-4 py-3 text-gray-400 text-sm">No results found</div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  const label = selectedValue ? selectedValue[labelKey] : placeholder;

  return (
    <>
      <div className="relative w-full" ref={triggerRef}>
        <button
          type="button"
          onClick={() => {
            if (!disabled) setOpen((s) => !s);
          }}
          disabled={disabled}
          className={`w-full border border-gray-300 rounded-lg p-2 pl-4 text-left flex items-center justify-between transition-all duration-200 ${
            open ? "ring-2 ring-primary" : ""
          } ${error ? "ring-red-400" : ""} ${
            disabled
              ? "bg-gray-100 cursor-not-allowed opacity-60 text-gray-400"
              : "bg-white text-gray-700 hover:border-primary focus:ring-2 focus:ring-primary"
          }`}
        >
          <div className="flex items-center gap-2">
            <i className={`${startIconClass} ${iconColor} text-lg`} />
            <span className="truncate">{label}</span>
          </div>

          <i className={`fa-solid fa-chevron-down ml-2 transition-transform duration-200 ${open ? "rotate-180" : ""} ${disabled ? "opacity-40" : "text-gray-500"}`} />
        </button>

        {error ? <p className="text-red-500 text-sm mt-1">{error}</p> : null}
      </div>

      {open && renderMenu()}
    </>
  );
};

export default SearchableDropdown;
